import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const EXPORT_TONE: Record<string, { bg: string; fg: string }> = {
  ready: { bg: "#e8f4ee", fg: "#1f7a4d" },
  pending: { bg: "#fbf1dd", fg: "#9a7b1f" },
  expired: { bg: "#f0efe9", fg: "#8a8e88" },
};

function ago(iso: string): string {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 60 * 24) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / (60 * 24))}d ago`;
}

export function Retention() {
  const { data, isLoading, error } = useQuery({ queryKey: ["retention"], queryFn: api.retention });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const card = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px" } as const;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Data & retention</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          Retention schedules, active legal holds, per-client data residency, and export jobs.
        </div>
      </div>

      {/* Legal holds — surfaced first; they override retention. */}
      {data.legalHolds.length > 0 && (
        <div style={{ ...card, boxShadow: "inset 0 0 0 1px rgba(217,119,87,.45)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Icon name="shield-alert" size={15} color="#c0612f" />
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Active legal holds</h2>
            <span style={{ fontSize: 11.5, color: "#8a8e88" }}>retention is suspended for held clients</span>
          </div>
          {data.legalHolds.map((h, i) => (
            <div key={h.id} style={{ display: "flex", gap: 10, padding: "7px 0", borderTop: i ? "1px solid #f4f3ee" : "none", fontSize: 12.5 }}>
              <span style={{ fontWeight: 600, minWidth: 120 }}>{h.client}</span>
              <span style={{ color: "#7a4a22", flex: 1 }}>{h.reason}</span>
              <span style={{ color: "#8a8e88", whiteSpace: "nowrap" }}>since {ago(h.since)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Retention rules */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Icon name="clock" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Retention schedule</h2>
        </div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 420 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 90px", padding: "6px 4px", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "#a8aca6" }}>
              <span>Scope</span><span>Data</span><span style={{ textAlign: "right" }}>Retain</span>
            </div>
            {data.rules.map((r) => (
              <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 90px", padding: "8px 4px", borderTop: "1px solid #f4f3ee", fontSize: 12.5, alignItems: "center" }}>
                <span style={{ color: r.scope === "Platform default" ? "#8a8e88" : "#1f231e" }}>{r.scope}</span>
                <span>{r.entity}</span>
                <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace", fontSize: 11.5 }}>{r.retain}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data residency per client */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Icon name="map" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Data residency</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
          {data.clients.map((cl) => (
            <div key={cl.id} style={{ border: "1px solid #e7e5df", borderRadius: 6, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{cl.name}</span>
                {cl.legalHold && <Icon name="shield-alert" size={13} color="#c0612f" />}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#16243a", background: "#e8ebf0", borderRadius: 4, padding: "2px 7px", fontFamily: "'Space Mono',monospace" }}>{cl.residency}</span>
                <span style={{ fontSize: 11, color: "#8a8e88", padding: "2px 0" }}>{cl.plan}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export jobs */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Icon name="database" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Client data exports</h2>
        </div>
        {data.exports.map((x, i) => {
          const tone = EXPORT_TONE[x.status] ?? { bg: "#f0efe9", fg: "#8a8e88" };
          return (
            <div key={x.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: i ? "1px solid #f4f3ee" : "none", fontSize: 12.5 }}>
              <span style={{ fontWeight: 600, minWidth: 120 }}>{x.client}</span>
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: tone.bg, color: tone.fg }}>{x.status}</span>
              <span style={{ color: "#8a8e88", flex: 1 }}>requested by {x.requestedBy}</span>
              <span style={{ color: "#8a8e88", whiteSpace: "nowrap" }}>{ago(x.at)}</span>
            </div>
          );
        })}
        {data.exports.length === 0 && <div style={{ padding: "8px 0", fontSize: 12.5, color: "#8a8e88" }}>No exports requested.</div>}
      </div>
    </Page>
  );
}
